/**
 * SectorDetailsModal.jsx — Drill-down modal for a single sector.
 *
 * Shows:
 *   - Normalised performance chart: portfolio sector holdings vs sector index
 *   - Holdings list for the sector
 *   - Period selector (1M / 3M / 6M / 1Y / 3Y)
 */

import React, { useState, useEffect } from 'react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
    Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { getSectorPerformance } from '../../services/portfolioService';

/* ── helpers ── */
const fmt = (n, d = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);
const pnlClass = (n) => (n > 0 ? 'pnl-positive' : n < 0 ? 'pnl-negative' : '');

const PERIODS = ['1M', '3M', '6M', '1Y', '3Y'];

const SectorDetailsModal = ({ sector, onClose }) => {
    const [period, setPeriod] = useState('1Y');
    const [perfData, setPerfData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!sector?.name) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await getSectorPerformance(sector.name, period);
                const data = res?.data ?? res;
                if (!cancelled) setPerfData(data);
            } catch {
                if (!cancelled) setPerfData(null);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [sector?.name, period]);

    if (!sector) return null;

    // Merge index & portfolio series by date for overlay chart
    const mergedSeries = [];
    if (perfData) {
        const indexMap = {};
        (perfData.indexSeries || []).forEach((p) => { indexMap[p.date] = p.indexValue; });
        const portfolioMap = {};
        (perfData.portfolioSeries || []).forEach((p) => { portfolioMap[p.date] = p.portfolioValue; });

        const allDates = [...new Set([
            ...Object.keys(indexMap),
            ...Object.keys(portfolioMap),
        ])].sort();

        for (const date of allDates) {
            mergedSeries.push({
                date,
                index: indexMap[date] ?? null,
                portfolio: portfolioMap[date] ?? null,
            });
        }
    }

    const holdings = sector.holdings || perfData?.holdings || [];

    return (
        <div className="sector-modal-overlay" onClick={onClose}>
            <div className="sector-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sector-modal-close" onClick={onClose} aria-label="Close">✕</button>

                {/* Header */}
                <div className="sector-modal-header">
                    <span className="sector-modal-dot" style={{ background: sector.color }} />
                    <div>
                        <h2 className="sector-modal-title">{sector.name}</h2>
                        <p className="sector-modal-subtitle">
                            {fmtCurrency(sector.value)} · {fmtPct(sector.percentage)} of portfolio
                            {sector.indexTicker && <span> · Index: {sector.indexTicker}</span>}
                        </p>
                    </div>
                </div>

                {/* Period selector */}
                <div className="sector-period-bar">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            className={`sector-period-btn ${period === p ? 'sector-period-btn--active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {/* Performance chart */}
                <div className="sector-chart-section">
                    <h4 className="sector-chart-label">
                        Performance Comparison (Base 100)
                    </h4>
                    {loading ? (
                        <div className="portfolio-loading" style={{ minHeight: 220 }}>Loading performance data…</div>
                    ) : mergedSeries.length > 0 ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={mergedSeries} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                <defs>
                                    <linearGradient id="sectorIdx" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="sectorPort" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={sector.color || '#7c3aed'} stopOpacity={0.25} />
                                        <stop offset="95%" stopColor={sector.color || '#7c3aed'} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    tickFormatter={(v) => {
                                        const d = new Date(v);
                                        return `${d.getDate()}/${d.getMonth() + 1}`;
                                    }}
                                    minTickGap={40}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    domain={['auto', 'auto']}
                                />
                                <Tooltip
                                    labelFormatter={(v) => new Date(v).toLocaleDateString('en-IN')}
                                    formatter={(val, name) => [
                                        val != null ? val.toFixed(1) : '—',
                                        name === 'index' ? 'Sector Index' : 'Your Holdings',
                                    ]}
                                    contentStyle={{
                                        background: 'var(--bg-card)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 8,
                                        fontSize: 12,
                                    }}
                                />
                                <Legend
                                    formatter={(value) => (value === 'index' ? 'Sector Index' : 'Your Holdings')}
                                    wrapperStyle={{ fontSize: 12 }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="index"
                                    stroke="#3b82f6"
                                    fill="url(#sectorIdx)"
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                />
                                <Area
                                    type="monotone"
                                    dataKey="portfolio"
                                    stroke={sector.color || '#7c3aed'}
                                    fill="url(#sectorPort)"
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="sector-no-data">
                            No performance data available yet. Sync daily to build comparison history.
                        </p>
                    )}
                </div>

                {/* Holdings list */}
                <div className="sector-holdings-section">
                    <h4 className="sector-chart-label">
                        Holdings in {sector.name} ({holdings.length})
                    </h4>
                    {holdings.length === 0 ? (
                        <p className="sector-no-data">No holdings in this sector.</p>
                    ) : (
                        <div className="holdings-table-wrap">
                            <table className="holdings-table sector-holdings-table">
                                <thead>
                                    <tr>
                                        <th>Instrument</th>
                                        <th>Qty</th>
                                        <th>Avg Cost</th>
                                        <th>Last Price</th>
                                        <th>Day Change</th>
                                        <th>Value</th>
                                        <th>P&L</th>
                                        <th>Weight</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {holdings.map((h) => (
                                        <tr key={h.instrumentId}>
                                            <td style={{ fontWeight: 600 }}>{h.name || h.instrumentId}</td>
                                            <td>{fmt(h.quantity, 0)}</td>
                                            <td>{fmtCurrency(h.avgCost)}</td>
                                            <td>{fmtCurrency(h.lastPrice)}</td>
                                            <td className={pnlClass(h.dayChange)}>
                                                {h.dayChangePct != null ? fmtPct(h.dayChangePct) : 'N/A'}
                                            </td>
                                            <td>{fmtCurrency(h.marketValue)}</td>
                                            <td className={pnlClass(h.unrealizedPnl)}>
                                                {fmtCurrency(h.unrealizedPnl)}
                                            </td>
                                            <td>{fmtPct(h.weight)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SectorDetailsModal;
